import { useCallback } from 'react';
import { toast } from 'sonner';

import { NotificationSeverityIcon } from '~/features/notifications/components/notification-severity-icon';
import {
  useInboxAutoRefresh,
  usePreferences,
} from '~/features/notifications/hooks/use-notifications';
import { useNotificationsStream } from '~/features/notifications/hooks/use-notifications-stream';
import type { NotificationStreamEvent } from '~/features/notifications/services/notifications-sse';
import { isInAppEnabled } from '~/features/notifications/types/notifications.api.types';
import { maskEmailsInText } from '~/utils/masking';

/**
 * Side-effect-only component: drives the notifications subsystem.
 *
 * 1. Subscribes to the SSE notifications stream — but only when the in-app
 *    channel is ACTIVE in `/preferences`. Disabling in-app in settings tears
 *    the stream down; re-enabling reopens it (the toggle invalidates the
 *    preferences query, which flips `enabled` here).
 * 2. Surfaces fresh events as Sonner toasts (toast-only — no query
 *    invalidations, which previously fanned out 20+ parallel inbox/unread-count
 *    requests on each reconnect's replay burst).
 * 3. Mounts {@link useInboxAutoRefresh} so the inbox + all-notifications
 *    queries refresh only when the polled unread count actually rises.
 *
 * Renders nothing — must live inside the authenticated tenant layout.
 */
export function NotificationsProvider() {
  useInboxAutoRefresh();

  // Preferences gate the stream: no point holding an SSE connection open when
  // the user has the in-app channel turned off. `data` is undefined while the
  // query is in flight → stream stays closed until we positively confirm the
  // channel is enabled.
  const { data: preferences } = usePreferences();
  const inAppEnabled = isInAppEnabled(preferences ?? []);

  // Stable callback so the stream effect doesn't tear down on every render.
  const handleUnavailable = useCallback(() => {
    toast.error('In-app notifications are unavailable right now. We’ll retry when you reload.', {
      id: 'notifications-stream-unavailable',
    });
  }, []);

  useNotificationsStream({
    enabled: inAppEnabled,
    onUnavailable: handleUnavailable,
    onNotification: (event) => {
      // Skip toast for replays — on (re)connect the backend resends events
      // the client may have missed. Toasting all of them would bombard the
      // user; lists catch up via the unread-count poll / count-delta gate.
      if (event.replay) return;

      const { title, body, icon } = event.card;
      const fire = pickToastVariant(event.severity);
      fire(title, {
        // Sonner dedupes by id — guards against duplicate fan-out from the backend.
        id: event.notification_id,
        // Body copy under the title; emails masked to match the inbox (AGENT-187).
        description: body ? maskEmailsInText(body) : undefined,
        // The card's own glyph (mapped from `icon`) replaces the variant's default.
        icon: <NotificationSeverityIcon severity={event.severity ?? 'low'} type="" icon={icon} />,
        // SSE notifications surface top-right to separate them from
        // bottom-right action feedback (mutations, form errors, etc.).
        position: 'top-right',
      });
    },
  });

  return null;
}

/** Map backend severity to a Sonner variant so high-severity events stand out visually. */
function pickToastVariant(severity: NotificationStreamEvent['severity']): typeof toast.info {
  switch (severity) {
    case 'critical':
    case 'high':
      return toast.error;
    case 'medium':
      return toast.warning;
    case 'low':
    default:
      return toast.info;
  }
}
